import { useState, useEffect, useRef } from 'react';

export default function useWebAudioVoiceEffects() {
  const [isConnected, setIsConnected] = useState(false);
  const [selectedEffect, setSelectedEffect] = useState('');
  const [processedStream, setProcessedStream] = useState(null);
  
  const audioContextRef = useRef(null);
  const sourceRef = useRef(null);
  const effectsChainRef = useRef(null);

  // Extensive voice effects library using Web Audio API
  const voiceEffects = [
    // Pitch & Formant Effects
    { id: 'robot', name: '🤖 Robot Voice', description: 'Classic robotic voice effect' },
    { id: 'deep', name: '👹 Deep Voice', description: 'Lower pitch masculine voice' },
    { id: 'chipmunk', name: '🐿️ Chipmunk', description: 'Higher pitch cute voice' },
    { id: 'monster', name: '👺 Monster', description: 'Terrifying deep distorted voice' },
    { id: 'baby', name: '👶 Baby Voice', description: 'Innocent high-pitched voice' },
    { id: 'elderly', name: '👴 Elderly', description: 'Aged voice with tremor' },
    { id: 'helium', name: '🎈 Helium', description: 'Extreme high pitch effect' },
    { id: 'giant', name: '🗿 Giant', description: 'Massive low-pitch voice' },
    
    // Character Voices
    { id: 'alien', name: '👽 Alien', description: 'Extraterrestrial modulated voice' },
    { id: 'demon', name: '😈 Demon', description: 'Evil overlord voice' },
    { id: 'angel', name: '😇 Angel', description: 'Heavenly ethereal voice' },
    { id: 'wizard', name: '🧙‍♂️ Wizard', description: 'Mystical magical voice' },
    { id: 'vampire', name: '🧛‍♂️ Vampire', description: 'Dark gothic voice' },
    { id: 'ghost', name: '👻 Ghost', description: 'Spooky otherworldly voice' },
    { id: 'cyborg', name: '🦾 Cyborg', description: 'Half-human machine voice' },
    { id: 'superhero', name: '🦸‍♂️ Superhero', description: 'Heroic commanding voice' },
    
    // Environmental Effects
    { id: 'underwater', name: '🌊 Underwater', description: 'Submerged bubbly effect' },
    { id: 'cave', name: '🏔️ Cave Echo', description: 'Large cavern reverb' },
    { id: 'radio', name: '📻 Radio', description: 'Old radio transmission' },
    { id: 'phone', name: '📞 Phone Call', description: 'Telephone line quality' },
    { id: 'megaphone', name: '📢 Megaphone', description: 'Bullhorn announcement' },
    { id: 'tunnel', name: '🚇 Tunnel', description: 'Underground echo effect' },
    { id: 'stadium', name: '🏟️ Stadium', description: 'Large venue reverb' },
    { id: 'forest', name: '🌲 Forest', description: 'Nature ambience filter' },
    
    // Sci-Fi & Technology
    { id: 'android', name: '🤖 Android', description: 'Artificial intelligence voice' },
    { id: 'terminator', name: '🦾 Terminator', description: 'Machine overlord voice' },
    { id: 'spaceship', name: '🚀 Spaceship', description: 'Intercom communication' },
    { id: 'hologram', name: '📡 Hologram', description: 'Projected transmission' },
    { id: 'matrix', name: '💊 Matrix', description: 'Digital reality voice' },
    { id: 'glitch', name: '📺 Glitch', description: 'Digital corruption effect' },
    
    // Musical & Artistic
    { id: 'echo', name: '🔊 Echo', description: 'Classic echo and reverb' },
    { id: 'chorus', name: '🎵 Chorus', description: 'Layered harmony effect' },
    { id: 'vocoder', name: '🎛️ Vocoder', description: 'Synthesized speech effect' },
    { id: 'flanger', name: '🌀 Flanger', description: 'Sweeping whoosh effect' },
    { id: 'phaser', name: '🔄 Phaser', description: 'Oscillating phase effect' },
    { id: 'harmonizer', name: '🎶 Harmonizer', description: 'Multi-pitch harmony' },
    
    // Distortion & Noise
    { id: 'distortion', name: '⚡ Distorted', description: 'Heavy distortion effect' },
    { id: 'bitcrusher', name: '🎮 8-bit', description: 'Retro video game voice' },
    { id: 'fuzz', name: '🔥 Fuzz', description: 'Fuzzy overdriven sound' },
    { id: 'static', name: '📺 Static', description: 'Radio static interference' },
    { id: 'broken', name: '💥 Broken Radio', description: 'Malfunctioning transmission' },
    
    // Animals & Creatures
    { id: 'dragon', name: '🐉 Dragon', description: 'Powerful mythical beast' },
    { id: 'tiger', name: '🐅 Tiger', description: 'Fierce predator growl' },
    { id: 'bee', name: '🐝 Bee Buzz', description: 'Buzzing insect effect' },
    { id: 'bird', name: '🐦 Bird Tweet', description: 'Chirpy bird-like voice' },
    { id: 'whale', name: '🐋 Whale Song', description: 'Deep ocean creature' },
    
    // Age & Gender Variations
    { id: 'teenager', name: '🧒 Teenager', description: 'Young adolescent voice' },
    { id: 'feminine', name: '👩 Feminine', description: 'Higher formant female voice' },
    { id: 'masculine', name: '👨 Masculine', description: 'Lower formant male voice' },
    { id: 'androgynous', name: '🧑 Androgynous', description: 'Gender-neutral voice' },
    
    // Special Effects
    { id: 'whisper', name: '🤫 Whisper', description: 'Quiet secretive voice' },
    { id: 'shout', name: '📣 Shout', description: 'Loud amplified voice' },
    { id: 'drunk', name: '🍺 Drunk', description: 'Intoxicated slurred speech' },
    { id: 'cold', name: '🤧 Cold/Stuffy', description: 'Congested nasal voice' },
    { id: 'sleepy', name: '😴 Sleepy', description: 'Tired drowsy voice' },
    { id: 'excited', name: '🎉 Excited', description: 'High-energy enthusiastic' }
  ];

  useEffect(() => {
    setIsConnected(true); // Web Audio API is always available in modern browsers
  }, []);

  // Create audio processing chain with extensive effects
  const createEffectsChain = (audioContext, effect) => {
    const chain = {};

    switch (effect) {
      // Basic Pitch Effects
      case 'robot':
        chain.bitCrusher = audioContext.createScriptProcessor(4096, 1, 1);
        chain.bitCrusher.onaudioprocess = (event) => {
          const input = event.inputBuffer.getChannelData(0);
          const output = event.outputBuffer.getChannelData(0);
          for (let i = 0; i < input.length; i++) {
            output[i] = Math.round(input[i] * 8) / 8;
          }
        };
        break;

      case 'deep':
      case 'monster':
      case 'giant':
      case 'dragon':
        chain.pitchShift = createPitchShifter(audioContext, 0.5); // Lower pitch
        if (effect === 'monster' || effect === 'dragon') {
          chain.distortion = audioContext.createWaveShaper();
          chain.distortion.curve = makeDistortionCurve(80);
        }
        break;

      case 'chipmunk':
      case 'baby':
      case 'helium':
        const pitchRatio = effect === 'helium' ? 2.5 : effect === 'baby' ? 1.8 : 1.5;
        chain.pitchShift = createPitchShifter(audioContext, pitchRatio);
        break;

      // Character Voices
      case 'alien':
        chain.oscillator = audioContext.createOscillator();
        chain.oscillator.frequency.value = 30;
        chain.gainNode = audioContext.createGain();
        chain.gainNode.gain.value = 50;
        chain.ringMod = audioContext.createGain();
        break;

      case 'demon':
      case 'vampire':
        chain.pitchShift = createPitchShifter(audioContext, 0.3);
        chain.distortion = audioContext.createWaveShaper();
        chain.distortion.curve = makeDistortionCurve(100);
        chain.lowpass = audioContext.createBiquadFilter();
        chain.lowpass.type = 'lowpass';
        chain.lowpass.frequency.value = 800;
        break;

      case 'angel':
        chain.reverb = createReverb(audioContext, 3);
        chain.chorus = createChorus(audioContext);
        chain.highpass = audioContext.createBiquadFilter();
        chain.highpass.type = 'highpass';
        chain.highpass.frequency.value = 200;
        break;

      case 'wizard':
        chain.phaser = createPhaser(audioContext);
        chain.delay = audioContext.createDelay(0.5);
        chain.delay.delayTime.value = 0.2;
        break;

      case 'ghost':
        chain.tremolo = createTremolo(audioContext, 8);
        chain.reverb = createReverb(audioContext, 4);
        chain.highpass = audioContext.createBiquadFilter();
        chain.highpass.type = 'highpass';
        chain.highpass.frequency.value = 400;
        break;

      case 'cyborg':
      case 'android':
      case 'terminator':
        chain.vocoder = createVocoder(audioContext);
        chain.bitCrusher = audioContext.createScriptProcessor(4096, 1, 1);
        break;

      // Environmental Effects
      case 'underwater':
        chain.lowpass = audioContext.createBiquadFilter();
        chain.lowpass.type = 'lowpass';
        chain.lowpass.frequency.value = 300;
        chain.chorus = createChorus(audioContext);
        break;

      case 'cave':
      case 'stadium':
        const roomSize = effect === 'stadium' ? 6 : 4;
        chain.reverb = createReverb(audioContext, roomSize);
        chain.delay = audioContext.createDelay(1.0);
        chain.delay.delayTime.value = effect === 'stadium' ? 0.8 : 0.5;
        break;

      case 'radio':
      case 'phone':
        chain.bandpass = audioContext.createBiquadFilter();
        chain.bandpass.type = 'bandpass';
        chain.bandpass.frequency.value = effect === 'phone' ? 1200 : 1000;
        chain.bandpass.Q.value = 2;
        chain.distortion = audioContext.createWaveShaper();
        chain.distortion.curve = makeDistortionCurve(20);
        break;

      case 'megaphone':
        chain.distortion = audioContext.createWaveShaper();
        chain.distortion.curve = makeDistortionCurve(60);
        chain.bandpass = audioContext.createBiquadFilter();
        chain.bandpass.type = 'bandpass';
        chain.bandpass.frequency.value = 800;
        break;

      case 'tunnel':
        chain.delay = audioContext.createDelay(1.0);
        chain.delay.delayTime.value = 0.4;
        chain.feedback = audioContext.createGain();
        chain.feedback.gain.value = 0.6;
        break;

      // Musical Effects
      case 'echo':
        chain.delay = audioContext.createDelay(1.0);
        chain.delay.delayTime.value = 0.3;
        chain.feedback = audioContext.createGain();
        chain.feedback.gain.value = 0.4;
        break;

      case 'chorus':
        chain.chorus = createChorus(audioContext);
        break;

      case 'flanger':
        chain.flanger = createFlanger(audioContext);
        break;

      case 'phaser':
        chain.phaser = createPhaser(audioContext);
        break;

      case 'vocoder':
        chain.vocoder = createVocoder(audioContext);
        break;

      // Distortion Effects
      case 'distortion':
      case 'fuzz':
        const amount = effect === 'fuzz' ? 80 : 50;
        chain.waveshaper = audioContext.createWaveShaper();
        chain.waveshaper.curve = makeDistortionCurve(amount);
        break;

      case 'bitcrusher':
        chain.bitCrusher = audioContext.createScriptProcessor(4096, 1, 1);
        chain.bitCrusher.onaudioprocess = (event) => {
          const input = event.inputBuffer.getChannelData(0);
          const output = event.outputBuffer.getChannelData(0);
          for (let i = 0; i < input.length; i++) {
            output[i] = Math.round(input[i] * 4) / 4; // More extreme bit crushing
          }
        };
        break;

      case 'glitch':
        chain.glitch = audioContext.createScriptProcessor(4096, 1, 1);
        let glitchCounter = 0;
        chain.glitch.onaudioprocess = (event) => {
          const input = event.inputBuffer.getChannelData(0);
          const output = event.outputBuffer.getChannelData(0);
          for (let i = 0; i < input.length; i++) {
            glitchCounter++;
            if (glitchCounter % 100 < 10) {
              output[i] = Math.random() * 0.1 - 0.05; // Random glitch
            } else {
              output[i] = input[i];
            }
          }
        };
        break;

      // Animal Effects
      case 'bee':
        chain.oscillator = audioContext.createOscillator();
        chain.oscillator.frequency.value = 200;
        chain.gainMod = audioContext.createGain();
        break;

      case 'bird':
        chain.pitchShift = createPitchShifter(audioContext, 2.0);
        chain.tremolo = createTremolo(audioContext, 15);
        break;

      case 'whale':
        chain.pitchShift = createPitchShifter(audioContext, 0.2);
        chain.reverb = createReverb(audioContext, 5);
        break;

      // Gender/Age Effects
      case 'feminine':
        chain.formantShift = createFormantShift(audioContext, 1.2);
        break;

      case 'masculine':
        chain.formantShift = createFormantShift(audioContext, 0.8);
        break;

      case 'teenager':
        chain.pitchShift = createPitchShifter(audioContext, 1.1);
        break;

      case 'elderly':
        chain.pitchShift = createPitchShifter(audioContext, 0.95);
        chain.tremolo = createTremolo(audioContext, 3);
        break;

      // Special Effects
      case 'whisper':
        chain.compressor = audioContext.createDynamicsCompressor();
        chain.compressor.threshold.value = -50;
        chain.compressor.ratio.value = 20;
        chain.gain = audioContext.createGain();
        chain.gain.gain.value = 0.3;
        break;

      case 'shout':
        chain.compressor = audioContext.createDynamicsCompressor();
        chain.compressor.threshold.value = -10;
        chain.gain = audioContext.createGain();
        chain.gain.gain.value = 2;
        break;

      case 'drunk':
        chain.wobble = audioContext.createScriptProcessor(4096, 1, 1);
        let wobblePhase = 0;
        chain.wobble.onaudioprocess = (event) => {
          const input = event.inputBuffer.getChannelData(0);
          const output = event.outputBuffer.getChannelData(0);
          for (let i = 0; i < input.length; i++) {
            wobblePhase += 0.001;
            const wobble = Math.sin(wobblePhase) * 0.3;
            output[i] = input[i] * (1 + wobble);
          }
        };
        break;

      default:
        return null;
    }

    return chain;
  };

  // Helper functions for advanced audio effects
  const makeDistortionCurve = (amount) => {
    const samples = 44100;
    const curve = new Float32Array(samples);
    const deg = Math.PI / 180;
    
    for (let i = 0; i < samples; i++) {
      const x = (i * 2) / samples - 1;
      curve[i] = ((3 + amount) * x * 20 * deg) / (Math.PI + amount * Math.abs(x));
    }
    
    return curve;
  };

  const createPitchShifter = (audioContext, ratio) => {
    const processor = audioContext.createScriptProcessor(4096, 1, 1);
    let buffer = [];
    let phase = 0;
    
    processor.onaudioprocess = (event) => {
      const input = event.inputBuffer.getChannelData(0);
      const output = event.outputBuffer.getChannelData(0);
      
      for (let i = 0; i < input.length; i++) {
        buffer.push(input[i]);
        phase += ratio;
        
        if (phase >= 1) {
          phase -= 1;
          if (buffer.length > 0) {
            output[i] = buffer.shift() || 0;
          }
        } else {
          output[i] = buffer.length > 0 ? buffer[0] : 0;
        }
      }
    };
    
    return processor;
  };

  const createReverb = (audioContext, roomSize = 3) => {
    const convolver = audioContext.createConvolver();
    const length = audioContext.sampleRate * roomSize;
    const impulse = audioContext.createBuffer(2, length, audioContext.sampleRate);
    
    for (let channel = 0; channel < 2; channel++) {
      const channelData = impulse.getChannelData(channel);
      for (let i = 0; i < length; i++) {
        channelData[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, 2);
      }
    }
    
    convolver.buffer = impulse;
    return convolver;
  };

  const createChorus = (audioContext) => {
    const delay1 = audioContext.createDelay(0.1);
    const delay2 = audioContext.createDelay(0.1);
    const osc1 = audioContext.createOscillator();
    const osc2 = audioContext.createOscillator();
    const gain1 = audioContext.createGain();
    const gain2 = audioContext.createGain();
    
    delay1.delayTime.value = 0.015;
    delay2.delayTime.value = 0.025;
    osc1.frequency.value = 0.5;
    osc2.frequency.value = 0.7;
    gain1.gain.value = 0.005;
    gain2.gain.value = 0.007;
    
    osc1.connect(gain1);
    osc2.connect(gain2);
    gain1.connect(delay1.delayTime);
    gain2.connect(delay2.delayTime);
    
    osc1.start();
    osc2.start();
    
    return { delay1, delay2 };
  };

  const createFlanger = (audioContext) => {
    const delay = audioContext.createDelay(0.02);
    const osc = audioContext.createOscillator();
    const gain = audioContext.createGain();
    
    delay.delayTime.value = 0.005;
    osc.frequency.value = 0.5;
    gain.gain.value = 0.003;
    
    osc.connect(gain);
    gain.connect(delay.delayTime);
    osc.start();
    
    return delay;
  };

  const createPhaser = (audioContext) => {
    const filter = audioContext.createBiquadFilter();
    const osc = audioContext.createOscillator();
    const gain = audioContext.createGain();
    
    filter.type = 'allpass';
    filter.frequency.value = 1000;
    osc.frequency.value = 0.5;
    gain.gain.value = 800;
    
    osc.connect(gain);
    gain.connect(filter.frequency);
    osc.start();
    
    return filter;
  };

  const createTremolo = (audioContext, speed = 5) => {
    const gain = audioContext.createGain();
    const osc = audioContext.createOscillator();
    const oscGain = audioContext.createGain();
    
    osc.frequency.value = speed;
    oscGain.gain.value = 0.5;
    gain.gain.value = 0.5;
    
    osc.connect(oscGain);
    oscGain.connect(gain.gain);
    osc.start();
    
    return gain;
  };

  const createVocoder = (audioContext) => {
    // Simplified vocoder using multiple bandpass filters
    const filters = [];
    const frequencies = [200, 400, 800, 1600, 3200, 6400];
    
    frequencies.forEach(freq => {
      const filter = audioContext.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.value = freq;
      filter.Q.value = 10;
      filters.push(filter);
    });
    
    return filters;
  };

  const createFormantShift = (audioContext, ratio) => {
    const filter1 = audioContext.createBiquadFilter();
    const filter2 = audioContext.createBiquadFilter();
    const filter3 = audioContext.createBiquadFilter();
    
    filter1.type = 'peaking';
    filter1.frequency.value = 800 * ratio;
    filter1.Q.value = 1;
    filter1.gain.value = 6;
    
    filter2.type = 'peaking';
    filter2.frequency.value = 1200 * ratio;
    filter2.Q.value = 1;
    filter2.gain.value = 4;
    
    filter3.type = 'peaking';
    filter3.frequency.value = 2400 * ratio;
    filter3.Q.value = 1;
    filter3.gain.value = 2;
    
    return { filter1, filter2, filter3 };
  };

  // Apply voice effect to audio stream
  const applyVoiceEffect = async (originalStream, effectId) => {
    try {
      // Clean up previous audio context
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }

      if (!effectId) {
        setProcessedStream(originalStream);
        return originalStream;
      }

      // Create new audio context
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioContext.createMediaStreamSource(originalStream);
      const destination = audioContext.createMediaStreamDestination();

      // Create effects chain
      const effects = createEffectsChain(audioContext, effectId);
      
      if (!effects) {
        source.connect(destination);
      } else {
        // Connect the effects chain
        let currentNode = source;

        switch (effectId) {
          case 'robot':
          case 'deep':
          case 'chipmunk':
            currentNode.connect(effects.bitCrusher || effects.pitchShift);
            (effects.bitCrusher || effects.pitchShift).connect(destination);
            break;

          case 'echo':
            // Wet signal (with delay)
            currentNode.connect(effects.delay);
            effects.delay.connect(effects.feedback);
            effects.feedback.connect(effects.delay);
            effects.delay.connect(effects.mix);
            
            // Dry signal (without delay)
            currentNode.connect(effects.mix);
            effects.mix.connect(destination);
            break;

          case 'distortion':
            currentNode.connect(effects.waveshaper);
            effects.waveshaper.connect(destination);
            break;

          case 'alien':
            currentNode.connect(effects.gainNode);
            effects.oscillator.connect(effects.gainNode.gain);
            effects.oscillator.start();
            effects.gainNode.connect(destination);
            break;

          case 'radio':
            currentNode.connect(effects.bandpass);
            effects.bandpass.connect(destination);
            break;

          default:
            currentNode.connect(destination);
        }
      }

      audioContextRef.current = audioContext;
      sourceRef.current = source;
      effectsChainRef.current = effects;

      const processedStream = destination.stream;
      setProcessedStream(processedStream);
      return processedStream;

    } catch (error) {
      console.error('Error applying voice effect:', error);
      return originalStream;
    }
  };

  // Cleanup
  const cleanup = () => {
    if (audioContextRef.current) {
      audioContextRef.current.close();
    }
  };

  useEffect(() => {
    return cleanup;
  }, []);

  return {
    voices: voiceEffects,
    isConnected,
    selectedEffect,
    setSelectedEffect,
    processedStream,
    applyVoiceEffect,
    cleanup
  };
} 